import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CompassIcon, MousePointerClickIcon } from 'lucide-react';
import { Logo } from '../Logo';
import { BumblebeePath } from './BumblebeePath';
import { ConceptOverlay } from './ConceptOverlay';
import { DevicePreview } from './DevicePreview';
import { StageOverlay } from './StageOverlay';
import { PulseExperience } from '../PulseExperience';
import { ResultsExperience } from '../results/ResultsExperience';
import { BoothExperience } from '../booth/BoothExperience';
import { campaignHero, campaignStages, type CampaignStage, type SnakId } from '../../data/campaign';
const ease = [0.22, 1, 0.36, 1] as const;

/** Natural row size at scale 1: two phones (0.62) + tablet (0.5) + gaps, plus labels and click pill. */
const ROW_W = 1260;
const DEVICE_H = 820 * 0.62;
const CHROME_H = 120;
function useFitScale(ref: React.RefObject<HTMLDivElement>) {
  const [k, setK] = useState(0.8);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const {
        width,
        height
      } = el.getBoundingClientRect();
      setK(Math.max(0.3, Math.min(1.1, width / ROW_W, (height - CHROME_H) / DEVICE_H)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return k;
}
function useOverlayPhoneScale() {
  const [s, setS] = useState(0.6);
  useEffect(() => {
    const update = () => setS(Math.max(0.35, Math.min(0.6, (window.innerHeight - 220) / 820)));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return s;
}
function renderSnak(id: SnakId, previewLoop = false) {
  if (id === 'congress') return <PulseExperience previewLoop={previewLoop} data-id="element-244" data-mp="3573bej53w2r3" />;
  if (id === 'results') return <ResultsExperience previewLoop={previewLoop} data-id="element-245" data-mp="3596arts5f88h" />;
  return <BoothExperience previewLoop={previewLoop} data-id="element-246" data-mp="3a77ri823qvgs" />;
}
export function CampaignPage() {
  const rowRef = useRef<HTMLDivElement>(null);
  const k = useFitScale(rowRef);
  const overlayPhoneScale = useOverlayPhoneScale();
  const [active, setActive] = useState<CampaignStage | null>(null);
  const [conceptOpen, setConceptOpen] = useState(false);
  return <main className="flex h-screen w-full flex-col overflow-hidden bg-mist-100 text-navy-900" data-id="element-247" data-mp="3c1bn3cf5aih1">
      <header className="mx-auto flex w-full max-w-[1400px] shrink-0 items-center justify-between px-8 pt-5" data-id="element-248" data-mp="376xvll3lsvhn">
        <div className="flex items-center gap-2.5" data-id="element-249" data-mp="3g59jhfd35f6e">
          <Logo width={24} height={24} data-id="element-250" data-mp="3g4qdmx0pmh8q" />
          <span className="text-[13px] font-semibold tracking-[0.18em]" data-id="element-251" data-mp="3uu61a44y26k7">SNAKKER</span>
          <span className="text-[13px] text-slate-400" data-id="element-252" data-mp="3uw8sgrhu25qi">for Ferring</span>
        </div>
        <span className="rounded-full border border-navy-900/10 bg-white px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-navy-700" data-id="element-253" data-mp="3pvupvoz03nzv">
          ESHRE 2027 · Campaign concept
        </span>
      </header>

      <section className="mx-auto w-full max-w-[1400px] shrink-0 px-8 pt-3 text-center" data-id="element-254" data-mp="3m1nqc2n3rcy">
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.1
      }} className="text-[11px] font-semibold uppercase tracking-[0.26em] text-pulse-teal" data-id="element-255" data-mp="ma2awdw7gfow">
          {campaignHero.eyebrow}
        </motion.p>
        <motion.h1 initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.15,
        ease
      }} className="mt-2 font-serif text-[44px] leading-[0.95] tracking-tight md:text-[64px]" data-id="element-256" data-mp="i90c1y67a5ng">
          THE IVF PULSE<span className="text-pulse-teal" data-id="element-257" data-mp="3qreoymi294uv">.</span>
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.3,
        ease
      }} className="mx-auto mt-3 max-w-3xl text-[17px] font-medium leading-snug text-navy-800 md:text-[20px]" data-id="element-258" data-mp="3urgooq48jo7i">
          {campaignHero.subtitle}
        </motion.p>
        <motion.button type="button" onClick={() => setConceptOpen(true)} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4,
        ease
      }} className="mt-4 inline-flex h-11 items-center gap-2 rounded-full bg-navy-900 px-6 text-[13px] font-semibold text-white shadow-lg shadow-navy-900/10 transition hover:bg-navy-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris focus-visible:ring-offset-2" data-id="element-259" data-mp="ry0rg7uvzjpe">
          <CompassIcon className="h-4 w-4 text-pulse-teal" data-id="element-260" data-mp="4c2m6knvfswn" />
          Explain the overall concept
        </motion.button>
      </section>

      <section aria-label="The three connected experiences" className="relative mx-auto flex min-h-0 w-full max-w-[1500px] flex-1 flex-col px-6" data-id="element-261" data-mp="3rez3bf1tnt59">
        <div className="relative h-[56px] shrink-0" data-id="element-262" data-mp="1uldxq52tw9nf">
          <BumblebeePath data-id="element-263" data-mp="1pmsxi5nktpyz" />
        </div>

        <div ref={rowRef} className="flex min-h-0 flex-1 items-center justify-center" data-id="element-264" data-mp="1pnu8ka4lecgp">
          <ol className="flex items-end justify-center" style={{
          gap: 56 * k
        }} data-id="element-265" data-mp="1x627o13wpsp3">
            {campaignStages.map((stage, i) => <motion.li key={stage.id} initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.5 + i * 0.12,
            ease
          }} className="flex flex-col items-center" data-id="element-266" data-mp="223bud8zwvq98" data-mpi={i}>
                <div className="mb-3 text-center" data-id="element-267" data-mp="1ea2dqkrdcf2v">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em]" style={{
                color: stage.accent
              }} data-id="element-268" data-mp="1j7vkbewl3b3o">
                    {stage.number} · {stage.moment}
                  </p>
                  <p className="mt-1 text-[18px] font-semibold leading-tight text-navy-900" data-id="element-269" data-mp="1s8jutzmrqtbt">{stage.name}</p>
                  <p className="text-[13px] text-slate-500" data-id="element-270" data-mp="2zkaiogap6hjk">{stage.verbs}</p>
                </div>

                <button type="button" onClick={() => setActive(stage)} aria-label={`Explore ${stage.name}`} className="group relative flex cursor-pointer flex-col items-center focus:outline-none" data-id="element-271" data-mp="33lcl15zwv9pr">
                  <span className="relative block rounded-[52px] transition duration-500 group-hover:-translate-y-2 group-focus-visible:ring-4 group-focus-visible:ring-pulse-iris/40" data-id="element-272" data-mp="1dhj2jmfx0igu">
                    <DevicePreview kind={stage.device} scale={stage.device === 'phone' ? 0.62 * k : 0.5 * k} data-id="element-273" data-mp="ktevoxllxzck">
                      {renderSnak(stage.id)}
                    </DevicePreview>
                    <span className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 ring-4 transition group-hover:opacity-100" style={{
                  ['--tw-ring-color' as string]: `${stage.accent}66`
                }} data-id="element-274" data-mp="ujgb70zwa9fv" />
                  </span>
                  <span className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-navy-900/10 bg-white px-4 py-2 text-[12px] font-semibold text-navy-900 shadow-sm transition group-hover:border-transparent group-hover:bg-navy-900 group-hover:text-white" data-id="element-275" data-mp="3t9zzlzzdu28t">
                    <span className="relative flex h-2 w-2" data-id="element-276" data-mp="1zqziax15m9q8">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{
                    backgroundColor: stage.accent
                  }} data-id="element-277" data-mp="1zsu7injgko04" />
                      <span className="relative inline-flex h-2 w-2 rounded-full" style={{
                    backgroundColor: stage.accent
                  }} data-id="element-278" data-mp="2sdu0fnai0x0j" />
                    </span>
                    Click to explore
                    <MousePointerClickIcon className="h-3.5 w-3.5" style={{
                  color: stage.accent
                }} data-id="element-279" data-mp="2ijlg3mvmgadg" />
                  </span>
                </button>
              </motion.li>)}
          </ol>
        </div>
      </section>

      <footer className="shrink-0 px-8 pb-4 pt-2 text-center" data-id="element-280" data-mp="1c1y7x48rgoib">
        <p className="text-[10px] leading-relaxed text-slate-400" data-id="element-281" data-mp="1bzc2ba5fkj2q">
          Creative campaign prototype for Ferring. Peer figures are illustrative, no personal data is collected, and all
          medical and product content is subject to Ferring medical, legal and regulatory review.
        </p>
      </footer>

      <ConceptOverlay open={conceptOpen} onClose={() => setConceptOpen(false)} onDeepDive={s => {
      setConceptOpen(false);
      setActive(s);
    }} data-id="element-282" data-mp="1tsn2otgofxed" />

      <StageOverlay stage={active} preview={active ? <DevicePreview key={active.id} kind={active.device} scale={active.device === 'phone' ? overlayPhoneScale : 0.3} data-id="element-284" data-mp="2hihed7p9ffx1">
              {renderSnak(active.id, true)}
            </DevicePreview> : null} onClose={() => setActive(null)} onNext={setActive} data-id="element-283" data-mp="2hgehc3powmit" />
    </main>;
}